/**
 * StaffResetPassword.js
 *
 * Two-phase staff password reset page.
 * Route: /admin/reset-password
 *
 * Phase A (no token in URL): Email request form
 *   -> POST /v1/staff/auth/reset-password/request
 *   -> Show confirmation (anti-enumeration: always positive message)
 *
 * Phase B (token in URL): New password form
 *   -> On mount: POST /v1/staff/auth/reset-password/validate
 *   -> On submit: POST /v1/staff/auth/reset-password/confirm
 *   -> On success: redirect to /admin/login
 *
 * Security:
 *   - Raw token only in URL params during active flow
 *   - Not stored in state beyond active session
 *   - All sessions revoked server-side on success
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080B12; color: #E2E8F0; font-family: 'DM Sans', sans-serif; }
  .sr-root { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #080B12; position: relative; overflow: hidden; }
  .sr-bg-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px); background-size: 60px 60px; pointer-events: none; }
  .sr-card { position: relative; z-index: 1; width: 100%; max-width: 440px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; padding: 48px 40px; backdrop-filter: blur(20px); animation: sr-fadeup 0.5s ease both; }
  @keyframes sr-fadeup { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  .sr-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 32px; }
  .sr-logo-mark { width: 36px; height: 36px; background: linear-gradient(135deg,#C9A84C,#8B0000); border-radius: 9px; display: flex; align-items: center; justify-content: center; font-family: 'Syne',sans-serif; font-weight: 800; font-size: 16px; color: #fff; flex-shrink: 0; }
  .sr-logo-text { font-family: 'Syne',sans-serif; font-weight: 700; font-size: 16px; color: #fff; }
  .sr-logo-sub { font-size: 10px; color: #C9A84C; letter-spacing: 2px; text-transform: uppercase; margin-top: 1px; }
  .sr-title { font-family: 'Syne',sans-serif; font-size: 22px; font-weight: 700; color: #fff; margin-bottom: 6px; }
  .sr-sub { font-size: 13px; color: rgba(255,255,255,0.4); line-height: 1.6; margin-bottom: 28px; }
  .sr-label { display: block; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; margin-top: 16px; }
  .sr-input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 12px 16px; font-size: 14px; color: #fff; font-family: 'DM Sans',sans-serif; outline: none; transition: border-color 0.2s; }
  .sr-input:focus { border-color: rgba(201,168,76,0.5); }
  .sr-btn { width: 100%; padding: 13px; background: linear-gradient(135deg,#C9A84C,#A0832E); border: none; border-radius: 10px; font-size: 14px; font-weight: 700; color: #080B12; cursor: pointer; font-family: 'Syne',sans-serif; margin-top: 20px; transition: opacity 0.2s; }
  .sr-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sr-err { font-size: 12px; color: #FCA5A5; margin-top: 10px; padding: 10px 14px; background: rgba(239,68,68,0.08); border-radius: 8px; border: 1px solid rgba(239,68,68,0.2); }
  .sr-ok { font-size: 12px; color: #4ADE80; margin-top: 10px; padding: 10px 14px; background: rgba(74,222,128,0.08); border-radius: 8px; border: 1px solid rgba(74,222,128,0.2); }
  .sr-back { display: block; text-align: center; margin-top: 20px; font-size: 12px; color: rgba(201,168,76,0.7); text-decoration: none; }
  .sr-back:hover { color: #C9A84C; }
  .pw-bar { height: 3px; border-radius: 2px; margin-top: 5px; transition: all 0.3s; }
  .sr-hint { font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 4px; }
  .sr-loader { display: flex; align-items: center; justify-content: center; padding: 40px; gap: 10px; color: rgba(255,255,255,0.3); font-size: 13px; }
`;

function pwStrength(pw) {
  if (!pw) return { score: 0, color: '#374151', label: '' };
  let s = 0;
  if (pw.length >= 12) s++;
  if (pw.length >= 16) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const map = [
    { color: '#EF4444', label: 'Weak' },
    { color: '#F59E0B', label: 'Fair' },
    { color: '#F59E0B', label: 'Fair' },
    { color: '#22C55E', label: 'Good' },
    { color: '#22C55E', label: 'Strong' },
    { color: '#10B981', label: 'Very strong' },
  ];
  return { score: s, ...map[Math.min(s, 5)] };
}

function Logo() {
  return (
    <div className="sr-logo">
      <div className="sr-logo-mark">VI</div>
      <div>
        <div className="sr-logo-text">VISINVOKE</div>
        <div className="sr-logo-sub">Staff Access</div>
      </div>
    </div>
  );
}

// Phase A: Request reset by email
function RequestPhase() {
  const [email, setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]     = useState(false);
  const [err, setErr]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setErr('Email is required.'); return; }
    setLoading(true); setErr('');
    try {
      await fetch(`${API}/v1/staff/auth/reset-password/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setSent(true);
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) return (
    <>
      <Logo />
      <div className="sr-title">Check Your Email</div>
      <div className="sr-sub">If your email is registered as a staff account, a reset link has been sent. The link expires in 2 hours.</div>
      <div className="sr-ok">Reset email sent. Check your inbox and spam folder.</div>
      <Link to="/admin/login" className="sr-back">Back to login</Link>
    </>
  );

  return (
    <>
      <Logo />
      <div className="sr-title">Reset Password</div>
      <div className="sr-sub">Enter your staff email address. If it exists, you will receive a secure reset link.</div>
      <form onSubmit={handleSubmit}>
        <label className="sr-label">Staff Email Address</label>
        <input className="sr-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="staff@visinvoke.com" autoFocus required/>
        {err && <div className="sr-err">{err}</div>}
        <button className="sr-btn" type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
      </form>
      <Link to="/admin/login" className="sr-back">Back to login</Link>
    </>
  );
}

// Phase B: Confirm reset with token
function ConfirmPhase({ token }) {
  const navigate = useNavigate();
  const [phase, setPhase]     = useState('validating'); // validating | form | submitting | success | invalid
  const [tokenErr, setTokenErr] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [err, setErr]           = useState('');
  const strength = pwStrength(password);

  const validate = useCallback(async () => {
    try {
      const r = await fetch(`${API}/v1/staff/auth/reset-password/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (r.ok && d.valid) {
        setPhase('form');
      } else {
        setTokenErr(d.error || 'Invalid or expired reset link.');
        setPhase('invalid');
      }
    } catch {
      setTokenErr('Unable to validate reset link. Please check your connection.');
      setPhase('invalid');
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 12) { setErr('Password must be at least 12 characters.'); return; }
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setPhase('submitting'); setErr('');
    try {
      const r = await fetch(`${API}/v1/staff/auth/reset-password/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const d = await r.json();
      if (r.ok) {
        setPhase('success');
      } else {
        setPhase('form');
        setErr(d.error || 'Reset failed. Please try again.');
      }
    } catch {
      setPhase('form');
      setErr('Network error. Please try again.');
    }
  };

  if (phase === 'validating') return (
    <>
      <Logo />
      <div className="sr-loader">Validating reset link...</div>
    </>
  );

  if (phase === 'invalid') return (
    <>
      <Logo />
      <div className="sr-title">Link Invalid</div>
      <div className="sr-err" style={{ marginTop: 0 }}>{tokenErr}</div>
      <Link to="/admin/reset-password" className="sr-back" style={{ marginTop: 16, display: 'block', textAlign: 'center', color: 'rgba(201,168,76,0.7)', fontSize: 12 }}>Request a new reset link</Link>
      <Link to="/admin/login" className="sr-back">Back to login</Link>
    </>
  );

  if (phase === 'success') return (
    <>
      <Logo />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(74,222,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div className="sr-title" style={{ marginBottom: 0 }}>Password Reset</div>
      </div>
      <div className="sr-sub">Your password has been changed. All previous sessions have been revoked for security.</div>
      <div className="sr-ok">Password updated successfully.</div>
      <button className="sr-btn" onClick={() => navigate('/admin/login')}>Proceed to Login</button>
    </>
  );

  return (
    <>
      <Logo />
      <div className="sr-title">Set New Password</div>
      <div className="sr-sub">Choose a strong password. All existing sessions will be revoked after reset.</div>
      <form onSubmit={handleSubmit}>
        <label className="sr-label">New Password <span style={{ color: '#EF4444' }}>*</span></label>
        <input className="sr-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 12 characters" autoComplete="new-password" required/>
        {password && (
          <>
            <div className="pw-bar" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }}/>
            <div className="sr-hint" style={{ color: strength.color }}>{strength.label}</div>
          </>
        )}
        <label className="sr-label">Confirm Password <span style={{ color: '#EF4444' }}>*</span></label>
        <input className="sr-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" autoComplete="new-password" required/>
        {confirm && password !== confirm && <div className="sr-hint" style={{ color: '#EF4444' }}>Passwords do not match</div>}
        {err && <div className="sr-err">{err}</div>}
        <button className="sr-btn" type="submit" disabled={phase === 'submitting' || !password || !confirm}>
          {phase === 'submitting' ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
      <Link to="/admin/login" className="sr-back">Back to login</Link>
    </>
  );
}

export default function StaffResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <>
      <style>{styles}</style>
      <div className="sr-root">
        <div className="sr-bg-grid"/>
        <div className="sr-card">
          {token ? <ConfirmPhase token={token}/> : <RequestPhase/>}
        </div>
      </div>
    </>
  );
}
